import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { serverUrl, getAuthHeader } from ".";

export const useTrainingList = () => {
  return useQuery({
    queryKey: ["training"],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/training/`,
        getAuthHeader()
      );
      return response.data;
    },
  });
};

export const useCreateTraining = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (training: any) => {
      const response = await axios.post(
        `${serverUrl}/training/`,
        training,
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training"] });
    },
  });
};

export const usePatchTraining = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (training: any) => {
      const response = await axios.patch(
        `${serverUrl}/training/${training.id}/`,
        training,
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training"] });
    },
  });
};

export const useDeleteTraining = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`${serverUrl}/training/${id}/`, getAuthHeader());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training"] });
    },
  });
};

export const useRegisterTraining = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await axios.post(
        `${serverUrl}/training/register`,
        { training_id: id },
        getAuthHeader()
      );
      queryClient.invalidateQueries({ queryKey: ["training"] });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student_registration"] });
    },
  });
};

export const useRetrieveTraining = ({ id }: { id?: number }) => {
  return useQuery({
    queryKey: ["training", id],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/training/${id}`,
        getAuthHeader()
      );
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCancelRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await axios.delete(
        `${serverUrl}/training/student_registration`,
        getAuthHeader()
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student_registration"] });
      queryClient.invalidateQueries({ queryKey: ["training"] });
    },
    onError: (e: any) => {
      alert(e.response.data?.detail);
    },
  });
};

export const useStudentRegistration = () => {
  return useQuery({
    queryKey: ["student_registration"],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/training/student_registration`,
        getAuthHeader()
      );
      return response.data;
    },
  });
};

export const useTrainingRegistration = ({ id }: { id: number }) => {
  return useQuery({
    queryKey: ["training_registration"],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/training/training_registration/${id}`,
        getAuthHeader()
      );
      return response.data;
    },
  });
};

export const useUploadAttendanceBook = ({ id }: { id: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File[]) => {
      const formData = new FormData();
      formData.append("attendance_book", file[0]);

      const response = await axios.post(
        `${serverUrl}/training/${id}/attendance_book`,
        formData,
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training", id] });
    },
  });
};

export const useDeleteGallery = ({ id }: { id: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (filename: string) => {
      await axios.delete(
        `${serverUrl}/training/${id}/gallery?filename=${filename}`,
        getAuthHeader()
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training", id] });
    },
  });
};

export const useUploadGallery = ({ id }: { id: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append("gallery", file);
      });

      const response = await axios.post(
        `${serverUrl}/training/${id}/gallery`,
        formData,
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training", id] });
    },
  });
};
